import { Connection, PublicKey, Transaction, SystemProgram } from '@solana/web3.js';
import {
  TOKEN_PROGRAM_ID,
  getAssociatedTokenAddressSync,
} from '@solana/spl-token';
import { keccak256, toBytes, encodeFunctionData, type Hex } from 'viem';

/**
 * Client-side escrow funding.
 *
 * The message body is hashed here and committed on-chain, which is what lets the
 * server later prove the escrow and the message it paid for are the same thing.
 * Fund first, then send: an escrow with no message is refundable, but a message
 * with no escrow would be a free pass into a priced inbox.
 */

export function hashBody(body: string): Hex {
  return keccak256(toBytes(body));
}

// ------------------------------------------------------------------ solana

const ESCROW_SEED = new TextEncoder().encode('escrow');
const VAULT_SEED = new TextEncoder().encode('vault');
const CONFIG_SEED = new TextEncoder().encode('config');

/** Anchor's 8-byte instruction discriminator: sha256("global:<name>")[0..8]. */
async function discriminator(name: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(`global:${name}`);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(digest).slice(0, 8);
}

export function deriveSolanaEscrow(args: {
  programId: PublicKey;
  sender: PublicKey;
  recipient: PublicKey;
  nonce: bigint;
}) {
  const nonceBuf = new Uint8Array(8);
  new DataView(nonceBuf.buffer).setBigUint64(0, args.nonce, true);

  const [escrow] = PublicKey.findProgramAddressSync(
    [ESCROW_SEED, args.sender.toBytes(), args.recipient.toBytes(), nonceBuf],
    args.programId,
  );
  const [vault] = PublicKey.findProgramAddressSync(
    [VAULT_SEED, escrow.toBytes()],
    args.programId,
  );
  const [config] = PublicKey.findProgramAddressSync([CONFIG_SEED], args.programId);
  return { escrow, vault, config, nonceBuf };
}

export async function buildSolanaFundTx(args: {
  connection: Connection;
  programId: PublicKey;
  usdcMint: PublicKey;
  sender: PublicKey;
  recipient: PublicKey;
  amountMicros: bigint;
  replyWindowSecs: number;
  messageHash: Hex;
  nonce: bigint;
}): Promise<Transaction> {
  const { escrow, vault, config, nonceBuf } = deriveSolanaEscrow(args);
  const senderAta = getAssociatedTokenAddressSync(args.usdcMint, args.sender, true);

  // fund(nonce: u64, amount: u64, reply_window_secs: i64, message_hash: [u8;32])
  const disc = await discriminator('fund');
  const data = new Uint8Array(8 + 8 + 8 + 8 + 32);
  const view = new DataView(data.buffer);
  data.set(disc, 0);
  data.set(nonceBuf, 8);
  view.setBigUint64(16, args.amountMicros, true);
  view.setBigInt64(24, BigInt(args.replyWindowSecs), true);
  data.set(toBytes(args.messageHash), 32);

  const keys = [
    { pubkey: config, isSigner: false, isWritable: false },
    { pubkey: escrow, isSigner: false, isWritable: true },
    { pubkey: vault, isSigner: false, isWritable: true },
    { pubkey: args.sender, isSigner: true, isWritable: true },
    { pubkey: args.recipient, isSigner: false, isWritable: false },
    { pubkey: args.usdcMint, isSigner: false, isWritable: false },
    { pubkey: senderAta, isSigner: false, isWritable: true },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
  ];

  const tx = new Transaction().add({ keys, programId: args.programId, data: Buffer.from(data) });
  tx.feePayer = args.sender;
  tx.recentBlockhash = (await args.connection.getLatestBlockhash()).blockhash;
  return tx;
}

// ---------------------------------------------------------------- ethereum

export const ESCROW_ABI = [
  {
    type: 'function',
    name: 'fund',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'recipient', type: 'address' },
      { name: 'nonce', type: 'uint256' },
      { name: 'amount', type: 'uint256' },
      { name: 'replyWindow', type: 'uint64' },
      { name: 'messageHash', type: 'bytes32' },
    ],
    outputs: [{ name: '', type: 'bytes32' }],
  },
] as const;

export const ERC20_APPROVE_ABI = [
  {
    type: 'function',
    name: 'approve',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
] as const;

export function encodeEthFund(args: {
  recipient: Hex;
  nonce: bigint;
  amountMicros: bigint;
  replyWindowSecs: number;
  messageHash: Hex;
}): Hex {
  return encodeFunctionData({
    abi: ESCROW_ABI,
    functionName: 'fund',
    args: [
      args.recipient,
      args.nonce,
      args.amountMicros,
      BigInt(args.replyWindowSecs),
      args.messageHash,
    ],
  });
}

export function encodeApprove(spender: Hex, amount: bigint): Hex {
  return encodeFunctionData({
    abi: ERC20_APPROVE_ABI,
    functionName: 'approve',
    args: [spender, amount],
  });
}

/** Random u64 so two messages to the same wallet never collide on a PDA/id. */
export function freshNonce(): bigint {
  const buf = new Uint8Array(8);
  crypto.getRandomValues(buf);
  // Clear the top bit: the Solana seed is u64 but the EVM id is uint256, and
  // keeping both inside 2^63 avoids any sign-related surprise in between.
  buf[0]! &= 0x7f;
  return new DataView(buf.buffer).getBigUint64(0, false);
}
